/**
 * Persists React Flow `viewport` (pan/zoom) per `canvasId` in `sessionStorage` with a
 * short debounce. Read via `readViewport` for eager restore in `useCanvasAutoFitViewport`.
 */

import type { Viewport } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useCanvasRoute } from "../context/CanvasRouteContext";

const STORAGE_PREFIX = "canvas-viewport:";

// Canvases whose tab was just closed. Their persisted viewport is cleared so a
// later reopen auto-fits instead of restoring a stale position. We also have to
// suppress the unmount/debounce flush below — without this, closing the active
// tab would immediately re-persist the very viewport we just cleared (the
// outgoing canvas's hook flushes on unmount). Cleared when the canvas is
// (re)opened, so a fresh session of that canvas persists normally again.
const suppressedCanvasIds = new Set<string>();

/** Synchronous read for initial paint (no hook); returns null if missing or invalid. */
export function readViewport(canvasId: string): Viewport | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + canvasId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed.x === "number" &&
      typeof parsed.y === "number" &&
      typeof parsed.zoom === "number"
    ) {
      return parsed as Viewport;
    }
    return null;
  } catch {
    return null;
  }
}

function writeViewport(canvasId: string, viewport: Viewport) {
  // A just-closed canvas must not have its viewport resurrected by a trailing
  // flush; persistence resumes once it is reopened (see the hook below).
  if (suppressedCanvasIds.has(canvasId)) return;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + canvasId, JSON.stringify(viewport));
  } catch {
    // quota exceeded or unavailable -- ignore
  }
}

/**
 * Clears a canvas's persisted viewport — call when its tab is closed so the
 * next open auto-fits to content rather than restoring the stale position.
 * Also suppresses any in-flight debounced/unmount write for that canvas so the
 * value can't be re-persisted by the outgoing hook; persistence resumes when
 * the canvas is reopened.
 */
export function clearViewport(canvasId: string) {
  suppressedCanvasIds.add(canvasId);
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + canvasId);
  } catch {
    // unavailable -- ignore
  }
}

/** Returns `savedViewport` for fit logic and `onViewportChange` for `<ReactFlow onMoveEnd>`. */
export function useCanvasViewportStorage() {
  const { canvasId } = useCanvasRoute();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingWriteRef = useRef<{
    canvasId: string;
    viewport: Viewport;
  } | null>(null);

  const savedViewport = useMemo(
    () => (canvasId ? readViewport(canvasId) : null),
    [canvasId],
  );

  // A freshly (re)opened canvas should persist its viewport again, even if a
  // prior close in this session suppressed it.
  useEffect(() => {
    if (canvasId) suppressedCanvasIds.delete(canvasId);
  }, [canvasId]);

  // Flush any pending debounced write when canvasId changes or on unmount,
  // so the outgoing canvas's last viewport position is never lost.
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      if (pendingWriteRef.current) {
        writeViewport(
          pendingWriteRef.current.canvasId,
          pendingWriteRef.current.viewport,
        );
        pendingWriteRef.current = null;
      }
    };
  }, [canvasId]);

  const onViewportChange = useCallback(
    (viewport: Viewport) => {
      if (!canvasId) return;
      clearTimeout(timerRef.current);
      pendingWriteRef.current = { canvasId, viewport };
      timerRef.current = setTimeout(() => {
        if (pendingWriteRef.current) {
          writeViewport(
            pendingWriteRef.current.canvasId,
            pendingWriteRef.current.viewport,
          );
          pendingWriteRef.current = null;
        }
      }, 200);
    },
    [canvasId],
  );

  return { savedViewport, onViewportChange };
}
